<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Payments & Invoices</h3>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search by order id, payment id, customer name, phone or email ..."
                    color="#e60040"
                    hide-details="auto"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col md="2">
                  <v-select
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusItems"
                    label="Status"
                    hide-details="auto"
                    prepend-inner-icon="mdi-toggle-switch-outline"
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>
                <v-col md="2">
                  <v-text-field
                    solo
                    rounded
                    type="date"
                    v-model="filter.from"
                    label="From"
                    hide-details="auto"
                    @change="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col md="2">
                  <v-text-field
                    solo
                    rounded
                    type="date"
                    v-model="filter.to"
                    label="To"
                    hide-details="auto"
                    @change="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 575px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="575"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left sr_no">Sr No</th>
                      <th class="text-left">Invoice</th>
                      <th class="text-left">Customer</th>
                      <th class="text-left">Plan</th>
                      <th class="text-left">Order Id</th>
                      <th class="text-left">Amount</th>
                      <th class="text-left">Coupon / Wallet</th>
                      <th class="text-left">Method</th>
                      <th class="text-left">Status</th>
                      <th class="text-left">Paid On</th>
                    </tr>
                  </thead>
                  <tbody v-if="transactions.length != 0">
                    <tr
                      v-for="(transaction, i) in transactions"
                      :key="`transaction_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': transactions.length - 1 == i,
                      }"
                    >
                      <td style="min-width: 85px">
                        {{ serialNoStarting + i + 1 }}
                      </td>
                      <td>
                        <v-btn
                          v-if="transaction.status === 'success'"
                          small
                          rounded
                          color="primary"
                          :loading="downloadingId === transaction.id"
                          :disabled="downloadingId !== null"
                          @click="downloadInvoice(transaction)"
                        >
                          <v-icon left small>mdi-download</v-icon> PDF
                        </v-btn>
                      </td>
                      <td style="min-width: 160px">
                        {{ customerName(transaction.user) }}
                        <div class="caption grey--text" v-if="transaction.user">
                          {{ transaction.user.phone || transaction.user.email }}
                        </div>
                      </td>
                      <td style="min-width: 160px">{{ planName(transaction.plan) }}</td>
                      <td>{{ transaction.order_id }}</td>
                      <td>₹ {{ transaction.amount }}</td>
                      <td style="min-width: 140px">
                        <div v-if="parseFloat(transaction.coupon_discount) > 0">
                          {{ transaction.coupon_code || "Coupon" }}: -₹
                          {{ transaction.coupon_discount }}
                        </div>
                        <div v-if="parseFloat(transaction.wallet_amount) > 0">
                          Wallet: ₹ {{ transaction.wallet_amount }}
                        </div>
                      </td>
                      <td>{{ transaction.payment_method }}</td>
                      <td>
                        <v-chip
                          small
                          dark
                          :color="statusColor(transaction.status)"
                          >{{ transaction.status }}</v-chip
                        >
                      </td>
                      <td style="min-width: 160px">
                        {{
                          formatDate(
                            transaction.completed_at || transaction.created_at
                          )
                        }}
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="100" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination rounded v-model="page" :length="paginationLength">
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-snackbar :value="!!invoiceError" @input="invoiceError = ''" :timeout="4000" color="error" rounded="pill">
        {{ invoiceError }}
      </v-snackbar>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import { fileNameFrom, saveBlob } from "../walkies-reports/reportUtils";
export default {
  name: "PaymentsPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        status: "success",
        from: "",
        to: "",
      },
      statusItems: [
        { text: "Success", value: "success" },
        { text: "Pending", value: "pending" },
        { text: "Failed", value: "failed" },
      ],
      transactions: [],
      page: 1,
      serialNoStarting: 0,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      downloadingId: null,
      invoiceError: "",
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    customerName(user) {
      if (!user) return "";
      const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
      return name || user.user_name || "";
    },
    planName(plan) {
      if (!plan) return "";
      return `${plan.no_of_dogs} Dog ${plan.no_of_walks} Walk ${plan.tier || ""}`;
    },
    statusColor(status) {
      return { success: "green", pending: "orange", failed: "red" }[status] || "grey";
    },
    async getData() {
      this.isLoading = true;
      const params = {
        page: this.page,
        rowsPerPage: this.rowsPerPage,
        search_keyword: this.filter.search_keyword || "",
        status: this.filter.status || "",
        from: this.filter.from || "",
        to: this.filter.to || "",
      };
      await axios
        .get(`plan_transactions/admin_list`, { params })
        .then((response) => {
          this.transactions = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
    async downloadInvoice(transaction) {
      this.downloadingId = transaction.id;
      try {
        const response = await axios.get(
          `plan_transactions/${this.encryptIt(transaction.id)}/invoice`,
          { responseType: "blob" }
        );
        saveBlob(
          response.data,
          fileNameFrom(
            response.headers["content-disposition"],
            `Invoice_${transaction.order_id}.pdf`
          )
        );
      } catch (error) {
        // The error body is a blob too: read the API's message out of it
        let message = "Could not download the invoice.";
        try {
          message = JSON.parse(await error.response.data.text()).message || message;
        } catch (e) {
          // keep the default message
        }
        this.invoiceError = message;
      }
      this.downloadingId = null;
    },
  },
};
</script>
